import React, { useEffect, useState } from "react";
import DynamicIcon from "../DynamicIcon";
import AddPoolElement from "../templates/AddPoolElement";
import CustomSelectElement from "../CustomSelectElement";
import { useSelector } from 'react-redux'
import { selectAccountData } from "../../redux/main/accountSlice";

const initialState = {
    default: false,
    bankName: "",
    bankDescription: "",
    ccNumber: "",
    abi: "",
    cab: "",
    paymentTypeId: "",
    paymentTypeLabel: ""
}

export default function CustomerBanksPool({ arrayData, addEditBank, deleteBank }) {
    // Funzione wrapper per gestire l'eliminazione e impostare automaticamente default=true se rimane un solo metodo
    const handleDeleteBank = (field, index) => {
        // Prima elimina il metodo di pagamento
        deleteBank(field, index);
        
        // Se dopo l'eliminazione rimarrebbe solo un elemento nell'array
        if (arrayData.length === 2) {
            // Determina l'indice dell'elemento che rimarrà
            const remainingIndex = index === 0 ? 1 : 0;
            const remainingBank = {...arrayData[remainingIndex]};
            
            // Se quell'elemento non è già impostato come default, impostiamolo
            if (!remainingBank.default) {
                remainingBank.default = true;
                console.log('Impostazione automatica default=true sull\'ultimo metodo di pagamento rimasto');
                
                // Aggiorna l'elemento con default=true
                setTimeout(() => {
                    addEditBank(field, remainingBank, remainingIndex);
                }, 100); // Piccolo timeout per assicurarsi che l'eliminazione sia completata
            }
        }
    };
    // Gestisco l'apertura e chiusura del form
    // Questo stato passerà ai figli
    const [isAdding, setIsAdding] = useState(false);
    // Gestisco gli input del form
    const [formInput, setFormInput] = useState(initialState);
    const handleFormInput = (event) => {
        const { id, value, checked } = event?.target;
        const fixedValue = id === 'default' ? checked : value;
        setFormInput(prevState => ({
            ...prevState,
            [id]: fixedValue
        }))
    }
    // Gestisco la modalità edit
    const editInitialState = {
        isEditMode: true,
        index: null
    }
    const [edit, setEdit] = useState(editInitialState)
    // Gestisco il settaggio di edit
    const handleSetEdit = (index) => {
        setEdit({
            isEditMode: true,
            index
        });
        setIsAdding(true);
    }
    // Se edit è impostato, imposto i valori dell'oggetto all'indice specificato in formInput
    useEffect(() => {
        if (edit.isEditMode && edit.index !== null) {
            setFormInput(arrayData[edit.index])
        }
    }, [edit]);
    // Gestisco l'annullamento della modalità edit (tornando alla modalità add e chiudendo il dropdown)
    const handleCancelEdit = () => {
        setEdit(editInitialState);
        setFormInput(initialState);
        setIsAdding(false);
    }
    // Se chiudo la modifica con lo switch, azzero formInput ed esco dalla modalità edit
    useEffect(() => {
        if (!isAdding) {
            setEdit(editInitialState);
            setFormInput(initialState);
        }
    }, [isAdding])

    // Controllo che sia inserito almeno l'iban
    const [isAllField, setIsAllField] = useState(false);
    const checkAllField = () => {
        const requiredFields = ['ccNumber'];
        const allRequiredFieldsValid = requiredFields.every(field => {
            const value = (formInput)[field];
            return value && value.trim() !== '';
        });
        setIsAllField(allRequiredFieldsValid);
    }
    useEffect(() => {
        checkAllField();
    }, [formInput])

    // Gestisco il tasto aggiungi, il quale deve anche chiudere la tendina di aggiunta indirizzo
    const handleFormSubmit = () => {
        // Crea una copia del formInput che potrebbe essere modificata
        let updatedFormInput = {...formInput};
        
        // Se è l'unico metodo di pagamento (nessun elemento nell'array o solo questo in modalità modifica)
        if (!arrayData || arrayData.length === 0 || (arrayData.length === 1 && edit.isEditMode)) {
            // Imposta automaticamente default a true
            updatedFormInput.default = true;
            console.log('Impostazione automatica default=true perché è l\'unico metodo di pagamento');
        }
        
        if (edit.isEditMode && edit.index !== null) {
            addEditBank('banksList', updatedFormInput, edit.index);
            setFormInput(initialState);
            setIsAdding(false);
        } else {
            addEditBank('banksList', updatedFormInput);
            setFormInput(initialState);
            setIsAdding(false);
        }
    }

    // Funzione per gestire il metodo di pagamento
    const accountData = useSelector(selectAccountData); // Questo mi server per risalire ai paymentsType dell'account
    const [defaultMethodId, setDefaultMethodId] = useState(null);
    useEffect(() => {
        // Salvo l'id dell'oggetto banca con default impostato su true
        // Se non ci sono oggetti rimane null
        // Se nessuno degli oggetti presenti ha default true, rimposta undefined
        const defaultPaymentType = arrayData?.length > 0 ?
                                        arrayData.find(bank => bank.default)?._id : null;
        setDefaultMethodId(defaultPaymentType);
    }, [arrayData])
    const handlePaymentType = (value, label) => {
        setFormInput((prevState) => ({
            ...prevState,
            paymentTypeId: value,
            paymentTypeLabel: label
        }))
    }
    return (
        <div className="w-full bg-slate-200 p-1">
            {/* Sezione aggiunta nuovo elemento */}
            <div className="bg-white p-4">
                <AddPoolElement title={edit.isEditMode && edit.index !== null ? 'MODIFICA METODO DI PAGAMENTO' : 'AGGIUNGI METODO DI PAGAMENTO'} isAdding={isAdding} setIsAdding={setIsAdding}>
                    <div className="mt-4 flex flex-col gap-4">
                        {/* Tipo di pagamento - spostato in cima */}
                        <div className="flex gap-2">
                            <div className="input-box flex-1">
                                <label htmlFor="paymentType">Tipo di pagamento</label>
                                <CustomSelectElement
                                    id="paymentsType"
                                    name="paymentsType"
                                    list={accountData?.settings.customerPaymentTypes}
                                    setter={handlePaymentType}
                                    defaultValue={formInput.paymentTypeId || defaultMethodId}
                                    valueKey="_id"
                                    labelKey="name"
                                />
                            </div>
                            <div className={`pl-4 flex gap-2 p-2 border rounded-lg items-center ${formInput.default ? 'border-green-400' : 'border-transparent'}`}>
                                <input type="checkbox" id="default" checked={formInput.default} onChange={handleFormInput} />
                                <label htmlFor="default">Default</label>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1"><label htmlFor="bankName">Nome Banca</label><input type="text" id="bankName" onChange={handleFormInput} value={formInput.bankName} /></div>
                            <div className="input-box flex-1"><label htmlFor="bankDescription">Descrizione</label><input type="text" id="bankDescription" onChange={handleFormInput} value={formInput.bankDescription} /></div>
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1"><label htmlFor="ccNumber">IBAN (Campo obbligatorio)</label><input type="text" id="ccNumber" onChange={handleFormInput} value={formInput.ccNumber} /></div>
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1"><label htmlFor="cab">CAB</label><input type="text" id="cab" onChange={handleFormInput} value={formInput.cab} /></div>
                            <div className="input-box flex-1"><label htmlFor="abi">ABI</label><input type="text" id="abi" onChange={handleFormInput} value={formInput.abi} /></div>
                        </div>
                        <div className="w-full flex justify-end mt-4 mb-4 pb-2">
                            {
                                isAllField ? (
                                    <div className="flex gap-2">
                                        {/* Pulsante Annulla (sempre presente) */}
                                        <button 
                                            type="button"
                                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors" 
                                            onClick={() => {
                                                if (edit.isEditMode && edit.index !== null) {
                                                    handleCancelEdit();
                                                } else {
                                                    setIsAdding(false);
                                                    setFormInput(initialState);
                                                }
                                            }}
                                        >
                                            <i className="fa-solid fa-times mr-2"></i>Annulla
                                        </button>
                                        
                                        {/* Pulsante Aggiungi/Aggiorna in base al contesto */}
                                        <button 
                                            type="button"
                                            className={`px-4 py-2 text-white rounded-md transition-colors ${edit.isEditMode && edit.index !== null ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                                            onClick={handleFormSubmit}
                                        >
                                            {edit.isEditMode && edit.index !== null ? (
                                                <><i className="fa-solid fa-check mr-2"></i>Aggiorna</>
                                            ) : (
                                                <><i className="fa-solid fa-plus mr-2"></i>Aggiungi</>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        type="button"
                                        className="px-4 py-2 text-gray-500 bg-gray-200 rounded-md cursor-not-allowed opacity-60"
                                        disabled
                                    >
                                        <i className="fa-solid fa-ban mr-2"></i>
                                        {edit.isEditMode && edit.index !== null ? 'Aggiorna' : 'Aggiungi'}
                                    </button>
                                )
                            }
                        </div>
                    </div>
                </AddPoolElement>
            </div>
            {/* POOL - Tabella di rendering elementi array */}
            <div className="p-4">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="h-8">
                            <th className="w-12">#</th>
                            <th className="">BANCA</th>
                            <th className="">DESCRIZIONE</th>
                            <th className="">IBAN</th>
                            <th className="">TIPO DI PAGAMENTO</th>
                            <th className="">CAB</th>
                            <th className="">ABI</th>
                            <th className="">DEFAULT</th>
                            <th className="">MODIFICA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            arrayData?.map((row, index) => {
                                return (
                                    < React.Fragment key={index} >
                                        <tr className="h-8 border-t border-slate-500 border-dashed">
                                            <td className="w-12">{index + 1}</td>
                                            <td className="">{row.bankName}</td>
                                            <td className="">{row.bankDescription}</td>
                                            <td className="">{row.ccNumber}</td>
                                            <td className="">{row.paymentTypeLabel}</td>
                                            <td className="">{row.cab}</td>
                                            <td className="">{row.abi}</td>
                                            <td className="">{row.default ? 'true' : 'false'}</td>
                                            <td className="flex gap-2">
                                                <div onClick={() => handleSetEdit(index)}><DynamicIcon iconClass="fa-solid fa-pen-to-square" /></div>
                                                <div onClick={() => handleDeleteBank('banksList', index)}><DynamicIcon iconClass="fa-solid fa-trash" /></div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )

                            })
                        }
                    </tbody>
                </table>
            </div>

        </div >
    )
}